import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, FileWarning, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FileUploader } from '@/components/import/file-uploader';
import { parseSalesCSV, type SalesCsvParseResult } from '@/lib/sales-csv-parser';
import { createReport } from '@/lib/api';
import type { AvailablePeriods, BrandStat, ReportStatus, SalesImportRow } from '@/lib/api';

type PeriodType = 'month' | 'quarter' | 'range';

interface FormState {
  brand: string;
  periodType: PeriodType;
  monthValue: string;
  quarterValue: string;
  rangeStart: string;
  rangeEnd: string;
  title: string;
  description: string;
  status: ReportStatus;
}

const DEFAULT_FORM: FormState = {
  brand: '',
  periodType: 'quarter',
  monthValue: '',
  quarterValue: '',
  rangeStart: '',
  rangeEnd: '',
  title: '',
  description: '',
  status: 'draft',
};

function formatMonthLabel(month: string): string {
  const [y, m] = month.split('-');
  return `T${parseInt(m!, 10)}/${y}`;
}

function formatQuarterLabel(q: string): string {
  const [y, n] = q.split('-Q');
  return `Q${n} ${y}`;
}

function quarterToRange(q: string): { start: string; end: string } | null {
  const m = q.match(/^(\d{4})-Q([1-4])$/);
  if (!m) return null;
  const year = m[1]!;
  const num = parseInt(m[2]!, 10);
  const startMonth = (num - 1) * 3 + 1;
  return {
    start: `${year}-${String(startMonth).padStart(2, '0')}`,
    end: `${year}-${String(startMonth + 2).padStart(2, '0')}`,
  };
}

function computePeriodRange(form: FormState): { start: string; end: string; label: string } | null {
  if (form.periodType === 'month' && form.monthValue) {
    return {
      start: form.monthValue,
      end: form.monthValue,
      label: formatMonthLabel(form.monthValue),
    };
  }
  if (form.periodType === 'quarter' && form.quarterValue) {
    const range = quarterToRange(form.quarterValue);
    if (!range) return null;
    return { start: range.start, end: range.end, label: formatQuarterLabel(form.quarterValue) };
  }
  if (form.periodType === 'range' && form.rangeStart && form.rangeEnd) {
    if (form.rangeStart > form.rangeEnd) return null;
    return {
      start: form.rangeStart,
      end: form.rangeEnd,
      label: `${form.rangeStart.split('-')[1]}/${form.rangeStart.split('-')[0]} → ${form.rangeEnd.split('-')[1]}/${form.rangeEnd.split('-')[0]}`,
    };
  }
  return null;
}

function suggestTitle(brand: string, periodLabel: string): string {
  if (!brand || !periodLabel) return '';
  return `${periodLabel} ${brand} Performance Review`;
}

export interface ReportCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brands: BrandStat[];
  available: AvailablePeriods | null;
  /** Called after successful create — list page should refresh + show new report */
  onCreated: () => void;
}

export function ReportCreateModal({ open, onOpenChange, brands, available, onCreated }: ReportCreateModalProps) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [titleTouched, setTitleTouched] = useState(false);
  const [csvParseResult, setCsvParseResult] = useState<SalesCsvParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setForm(DEFAULT_FORM);
      setTitleTouched(false);
      setCsvParseResult(null);
      setError(null);
    }
  }, [open]);

  const periodRange = useMemo(() => computePeriodRange(form), [form]);

  // Auto-suggest title when brand or period changes (and user hasn't manually edited)
  useEffect(() => {
    if (titleTouched) return;
    const suggested = suggestTitle(form.brand, periodRange?.label ?? '');
    setForm((prev) => ({ ...prev, title: suggested }));
  }, [form.brand, periodRange?.label, titleTouched]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileSelected = async (file: File) => {
    setError(null);
    setCsvParseResult(null);
    setParsing(true);
    try {
      const result = await parseSalesCSV(file);
      setCsvParseResult(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.brand) {
      setError('Vui lòng chọn hãng');
      return;
    }
    if (!periodRange) {
      setError('Vui lòng chọn period hợp lệ');
      return;
    }
    if (!form.title.trim()) {
      setError('Vui lòng nhập tiêu đề');
      return;
    }

    const salesRows: SalesImportRow[] | undefined =
      csvParseResult && csvParseResult.rows.length > 0 ? csvParseResult.rows : undefined;

    setSubmitting(true);
    try {
      await createReport({
        brand: form.brand,
        period_start: periodRange.start,
        period_end: periodRange.end,
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        status: form.status,
        sales_rows: salesRows,
      });
      onCreated();
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = form.brand && periodRange && form.title.trim() && !submitting;
  const csvHasErrors = csvParseResult && csvParseResult.errors.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tạo báo cáo mới</DialogTitle>
          <DialogDescription>
            Báo cáo gắn với 1 hãng × period. Snapshot data sẽ được frozen tại thời điểm tạo. CSV sales (optional) sẽ UPSERT vào sales_monthly trước khi compute snapshot.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Brand */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Hãng <span className="text-destructive">*</span>
            </label>
            <Select value={form.brand} onValueChange={(v) => updateField('brand', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Chọn hãng..." />
              </SelectTrigger>
              <SelectContent>
                {brands.map((b) => (
                  <SelectItem key={b.brand} value={b.brand}>
                    {b.brand} <span className="ml-1 text-muted-foreground">({b.count} SKU)</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Period type */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Period <span className="text-destructive">*</span>
            </label>
            <div className="flex gap-1.5">
              {(['month', 'quarter', 'range'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => updateField('periodType', t)}
                  className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    form.periodType === t
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background text-muted-foreground hover:bg-accent'
                  }`}
                >
                  {t === 'month' ? 'Tháng' : t === 'quarter' ? 'Quý' : 'Range'}
                </button>
              ))}
            </div>

            {form.periodType === 'month' && (
              <Select value={form.monthValue} onValueChange={(v) => updateField('monthValue', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn tháng..." />
                </SelectTrigger>
                <SelectContent>
                  {(available?.months ?? []).map((m) => (
                    <SelectItem key={m} value={m}>
                      {formatMonthLabel(m)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {form.periodType === 'quarter' && (
              <Select value={form.quarterValue} onValueChange={(v) => updateField('quarterValue', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Chọn quý..." />
                </SelectTrigger>
                <SelectContent>
                  {(available?.quarters ?? []).map((q) => (
                    <SelectItem key={q} value={q}>
                      {formatQuarterLabel(q)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {form.periodType === 'range' && (
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.rangeStart} onValueChange={(v) => updateField('rangeStart', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Từ tháng..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(available?.months ?? []).map((m) => (
                      <SelectItem key={m} value={m}>
                        {formatMonthLabel(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={form.rangeEnd} onValueChange={(v) => updateField('rangeEnd', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Đến tháng..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(available?.months ?? []).map((m) => (
                      <SelectItem key={m} value={m}>
                        {formatMonthLabel(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {periodRange && (
              <p className="text-[11px] text-muted-foreground">
                Range: <span className="font-mono">{periodRange.start}</span> → <span className="font-mono">{periodRange.end}</span>
              </p>
            )}
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <label htmlFor="report-title" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tiêu đề <span className="text-destructive">*</span>
            </label>
            <Input
              id="report-title"
              value={form.title}
              onChange={(e) => {
                setTitleTouched(true);
                updateField('title', e.target.value);
              }}
              placeholder="Auto-suggest theo brand × period"
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label htmlFor="report-desc" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Mô tả (tùy chọn)
            </label>
            <Textarea
              id="report-desc"
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Ghi chú nội bộ, ngữ cảnh báo cáo..."
              maxLength={1000}
              className="min-h-[80px]"
            />
          </div>

          {/* Status */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
            <Select value={form.status} onValueChange={(v) => updateField('status', v as ReportStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft (chưa share được)</SelectItem>
                <SelectItem value="published">Published (sẵn sàng share)</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* CSV uploader */}
          <div className="space-y-1.5 border-t pt-4">
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Sales CSV (tùy chọn)
            </label>
            <p className="text-[11px] text-muted-foreground">
              Nếu upload, hệ thống sẽ UPSERT data vào <code className="rounded bg-muted px-1 py-0.5">sales_monthly</code> trước khi compute snapshot. Bỏ qua nếu data đã có sẵn trong DB cho period này.
            </p>
            <FileUploader onFileSelected={handleFileSelected} disabled={parsing || submitting} />

            {parsing && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Đang parse CSV...
              </div>
            )}

            {csvParseResult && csvParseResult.rows.length > 0 && (
              <Alert variant="success">
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>{csvParseResult.rows.length} dòng valid</AlertTitle>
                <AlertDescription className="text-xs">
                  Sẽ UPSERT vào sales_monthly khi tạo báo cáo.
                  {csvParseResult.warnings.length > 0 && (
                    <span className="block mt-1 text-muted-foreground">
                      ⚠ {csvParseResult.warnings.length} cảnh báo (xem console).
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {csvHasErrors && (
              <Alert variant="destructive">
                <FileWarning className="h-4 w-4" />
                <AlertTitle>CSV có {csvParseResult.errors.length} lỗi</AlertTitle>
                <AlertDescription className="text-xs">
                  <ul className="ml-4 list-disc">
                    {csvParseResult.errors.slice(0, 3).map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Hủy
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Tạo báo cáo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
